import React, { useState } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import Logo from '../common/Logo';

import { devError } from '@/utils/devLog';
const { Link } = ReactRouterDOM as any;

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setLoading(true);
      setError('');
      try {
        await api.subscribeToNewsletter(email);
        setSubscribed(true);
        setEmail('');
        setTimeout(() => {
            setSubscribed(false);
        }, 5000);
      } catch (err: any) {
        devError("Newsletter subscription error", err);
        setError(t('footer.newsletter.error'));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 pt-16 pb-24 md:pb-12 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 lg:gap-8">
          
          <div className="col-span-1 sm:col-span-2 lg:col-span-1 space-y-6 text-center lg:text-left rtl:lg:text-right">
            <div className="flex items-center justify-center lg:justify-start">
              <Logo location="footer" size="medium" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed max-w-xs mx-auto lg:mx-0">
              {t('footer.brand_desc')}
            </p>
            
            <div className="flex flex-col space-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 w-fit mx-auto lg:mx-0">
                <div className="flex items-center space-x-1 rtl:space-x-reverse">
                    <StarIcon className="w-5 h-5 text-green-500 fill-current" />
                    <StarIcon className="w-5 h-5 text-green-500 fill-current" />
                    <StarIcon className="w-5 h-5 text-green-500 fill-current" />
                    <StarIcon className="w-5 h-5 text-green-500 fill-current" />
                    <StarIcon className="w-5 h-5 text-green-500 fill-current" />
                </div>
                <div className="flex items-center text-xs font-medium text-gray-700 dark:text-gray-300">
                    <span className="font-bold mr-1 rtl:ml-1 rtl:mr-0">{t('footer.rating.excellent')}</span>
                    <span>4.9 {t('footer.rating.based_on')} <span className="underline decoration-gray-400">Trustpilot</span></span>
                </div>
            </div>

            <div className="flex justify-center lg:justify-start space-x-4 pt-2 rtl:space-x-reverse">
                <SocialLink href="#" icon={<FacebookIcon />} />
                <SocialLink href="#" icon={<InstagramIcon />} />
                <SocialLink href="#" icon={<LinkedinIcon />} />
            </div>
          </div>

          <div className="col-span-1 text-center lg:text-left rtl:lg:text-right">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 border-b border-gray-100 dark:border-gray-800 pb-2 inline-block lg:border-0 lg:pb-0">{t('footer.sections.navigation')}</h3>
            <ul className="space-y-3">
                <li><Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('nav.home')}</Link></li>
                <li><Link to="/services" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('nav.services')}</Link></li>
                <li><Link to="/about" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('nav.about')}</Link></li>
                <li><Link to="/contact" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('nav.contact')}</Link></li>
            </ul>
          </div>

          <div className="col-span-1 text-center lg:text-left rtl:lg:text-right">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 border-b border-gray-100 dark:border-gray-800 pb-2 inline-block lg:border-0 lg:pb-0">{t('footer.sections.legal')}</h3>
            <ul className="space-y-3">
                <li><Link to="/faq" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('footer.links.faq')}</Link></li>
                <li><Link to="/terms" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('footer.links.terms')}</Link></li>
                <li><Link to="/privacy" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('footer.links.privacy')}</Link></li>
                <li><Link to="/refund" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('footer.links.refund')}</Link></li>
                <li><Link to="/delivery" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 text-sm transition-colors">{t('footer.links.delivery')}</Link></li>
            </ul>
          </div>

          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 text-center lg:text-left rtl:lg:text-right">{t('footer.sections.newsletter')}</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 text-center lg:text-left rtl:lg:text-right">
              {t('footer.newsletter.desc')}
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col space-y-2 max-w-sm mx-auto lg:mx-0">
                <input 
                    type="email" 
                    placeholder={t('footer.newsletter.placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm outline-none transition-all dark:text-white placeholder-gray-400 ${error ? 'border-red-500 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'}`}
                    required
                />
                <button 
                    type="submit" 
                    disabled={loading}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-200 ${subscribed ? 'bg-green-500 hover:bg-green-600' : 'bg-primary-500 hover:bg-primary-400'} ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                    {subscribed ? t('footer.newsletter.btn.success') : (loading ? t('footer.newsletter.btn.loading') : t('footer.newsletter.btn.subscribe'))}
                </button>
                {error && <p className="text-xs text-red-500 text-center lg:text-left rtl:lg:text-right">{error}</p>}
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 text-center lg:text-left rtl:lg:text-right">
                {t('footer.download_app.title')}
              </p>
              <div className="flex flex-row items-stretch justify-between gap-2 max-w-[320px] mx-auto lg:mx-0 rtl:flex-row-reverse">
                {/* Google Play Store — à gauche */}
                <a
                  href={(import.meta as any).env?.VITE_PLAY_STORE_URL || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-lg transition-opacity hover:opacity-90"
                  aria-label={t('footer.download_app.cta')}
                >
                  <img
                    src="https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/darkAndroid.png"
                    alt=""
                    role="presentation"
                    className="h-10 w-full max-h-12 object-contain object-center dark:hidden"
                  />
                  <img
                    src="https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/lightAndroid.png"
                    alt=""
                    role="presentation"
                    className="h-10 w-full max-h-12 object-contain object-center hidden dark:block"
                  />
                  <span className="sr-only">{t('footer.download_app.cta')}</span>
                </a>
                {/* Apple App Store — à droite */}
                <a
                  href={(import.meta as any).env?.VITE_APP_STORE_URL || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-lg transition-opacity hover:opacity-90"
                  aria-label={t('footer.download_app.apple')}
                >
                  <img
                    src="https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/darkApple.png"
                    alt=""
                    role="presentation"
                    className="h-10 w-full max-h-12 object-contain object-center dark:hidden"
                  />
                  <img
                    src="https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/lightApple.png"
                    alt=""
                    role="presentation"
                    className="h-10 w-full max-h-12 object-contain object-center hidden dark:block"
                  />
                  <span className="sr-only">{t('footer.download_app.apple')}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800 flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center md:text-left rtl:md:text-right">
            &copy; {new Date().getFullYear()} eServices. {t('footer.copyright')}
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 rtl:space-x-reverse text-sm text-gray-500 dark:text-gray-400 font-medium">
             <Link to="/legal" className="hover:text-primary-600 transition-colors py-1">{t('footer.links.legal_notice')}</Link>
             <Link to="/cookies" className="hover:text-primary-600 transition-colors py-1">{t('footer.links.cookies')}</Link>
             <Link to="/rate-limiting" className="hover:text-primary-600 transition-colors py-1">{t('footer.links.rate_limiting')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const SocialLink: React.FC<{ href: string; icon: React.ReactNode }> = ({ href, icon }) => (
    <a 
        href={href} 
        className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-primary-100 dark:hover:bg-primary-900 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
    >
        {icon}
    </a>
);

const StarIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);

const FacebookIcon = () => (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
    </svg>
);

const InstagramIcon = () => (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.153-1.772 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 015.468.99c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
    </svg>
);

const LinkedinIcon = () => (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
    </svg>
);

export default Footer;